'use strict';

/**
 * The SIDE INSTANCE classifier (OWNER-RULINGS-WALK-BUILDS SIDE-INSTANCE, N45, 2026-10-08).
 *
 * A side instance is a second, local copy of an API — usually a branch's code — served beside the Dev Conductor's
 * own instance so a measure or an e2e walk can reach that branch. It is declared, never inferred:
 *
 *     NODE_ENV=development PUFFIN_SIDE_INSTANCE=1 PORT=4950 pnpm --filter planner-api dev
 *
 * and its web dev server is pointed at it with
 *
 *     PUFFIN_SIDE_API_PORT=4950 pnpm --filter planner-web exec vite --port 5790
 *
 * What the declaration means, and why each part is required:
 *
 *   - It is LOCAL DEVELOPMENT, exactly: `NODE_ENV` must be `development`. `test` would turn dev-test login off (the
 *     reason a side instance exists is that a browser walk can sign in), and anything else is a deployed or unknown
 *     tier, where a side instance has no meaning. The judgement is an allow-list, so an unset or typo'd `NODE_ENV`
 *     REFUSES (fail closed) — never `NODE_ENV === 'production'`, which would let `preview` (ParcelPuffin's
 *     production) through.
 *   - It listens OUTSIDE the Port Allocation Standard, in {@link SIDE_API_PORT_RANGE}: the standard ports belong to the
 *     Conductor's instances, whose web dev servers proxy to them. A side instance on a standard port either fails to
 *     bind or, worse, silently takes the main web's traffic when the Conductor's instance is down.
 *   - It runs NO background work — no cron scheduler, sweep, poller, signal subscriber or queue consumer. Two
 *     instances of one API on one box would otherwise both run them against the one dev = prod database, the shared
 *     local Redis and the same disk. The boot wrapper that enforces this lives beside the other boot gates,
 *     `@puffin-core/shared-config/lib/boot/sideInstance.cjs` (`startBackgroundWork`); this module only classifies.
 *
 * Exact-match, no normalization (`PUFFIN_SIDE_INSTANCE` is exactly `1`, a port is a plain decimal integer): a value
 * that is almost right is refused by name, never guessed.
 *
 * @module @puffin-core/env/sideInstance
 */

const { processEnv } = require('./processEnv');

const { isDevelopmentEnv } = require('./nodeEnv');

/** The ONE flag that declares a side instance. */
const SIDE_INSTANCE_FLAG = 'PUFFIN_SIDE_INSTANCE';

/** Its only accepted value. */
const SIDE_INSTANCE_FLAG_VALUE = '1';

/** The web dev server's declaration of the side API it proxies to. */
const SIDE_API_PORT_ENV = 'PUFFIN_SIDE_API_PORT';

/**
 * The ports a side API instance may listen on. Disjoint from the Port Allocation Standard (every API in 3001–4100,
 * every web app in 5173–5903, Metro on 8081, the retired 3900 / 4000 / 5901 / 5902) and from the Vite preview default
 * (4173); `@puffin-core/shared-config`'s `devServerConfig` pins the disjointness against its allocation list.
 */
const SIDE_API_PORT_RANGE = Object.freeze({ min: 4900, max: 4999 });

/** How a refusal names the range. */
const RANGE_LABEL = `${SIDE_API_PORT_RANGE.min}–${SIDE_API_PORT_RANGE.max}`;

/** A plain decimal port: digits only, no sign, no space, no fraction. */
const DECIMAL_PORT = /^[0-9]+$/;

/** A refused side-instance declaration. A boot or a dev-server config that hits one must not go on. */
class SideInstanceRefusal extends Error {
  /** @param {string} message */
  constructor(message) {
    super(`side instance refused: ${message}`);
    this.name = 'SideInstanceRefusal';
    this.code = 'SIDE_INSTANCE_REFUSED';
  }
}

/**
 * @param {*} port
 * @returns {boolean} true only for an integer inside {@link SIDE_API_PORT_RANGE}
 */
function isSideApiPort(port) {
  return (
    Number.isInteger(port) && port >= SIDE_API_PORT_RANGE.min && port <= SIDE_API_PORT_RANGE.max
  );
}

/**
 * @param {string} raw - a non-empty environment value
 * @param {string} label - how the refusal names it (`PORT "3500"`)
 * @returns {number} the side port
 * @throws {SideInstanceRefusal} not a plain decimal integer inside the side range
 */
function parseSidePort(raw, label) {
  const port = DECIMAL_PORT.test(raw) ? Number(raw) : Number.NaN;
  if (!isSideApiPort(port)) {
    throw new SideInstanceRefusal(
      `${label} is not in the side API port range ${RANGE_LABEL}. The Port Allocation Standard's ports belong ` +
        'to the Dev Conductor; a side instance listens beside them.',
    );
  }
  return port;
}

/**
 * @param {NodeJS.ProcessEnv} env
 * @returns {string} how a refusal names the runtime's NODE_ENV
 */
function describeNodeEnv(env) {
  return env.NODE_ENV === undefined
    ? 'NODE_ENV is unset'
    : `NODE_ENV is ${JSON.stringify(env.NODE_ENV)}`;
}

/**
 * Is this process DECLARED a side instance? The question a background-work seam asks (`@puffin-core/jobs`'
 * `JobWorker.start()` and DLQ gauge): it reads the flag and the runtime, never the port. Where the instance listens is
 * the boot's judgement ({@link resolveSideInstance}, through the side-effect posture, before the server listens); a
 * consumer that starts while its app is still being assembled must decline for a declared side instance whatever its
 * `PORT` says, and it has no port of its own to hand over.
 *
 * @param {NodeJS.ProcessEnv} [env=processEnv()]
 * @returns {boolean} `false` when the flag is absent; `true` for a declaration under `development`
 * @throws {SideInstanceRefusal} a flag that is not exactly `1`; a runtime that is not exactly `development`
 */
function isSideInstance(env = processEnv()) {
  const flag = env[SIDE_INSTANCE_FLAG];
  if (flag === undefined || flag === '') {
    return false;
  }
  if (flag !== SIDE_INSTANCE_FLAG_VALUE) {
    throw new SideInstanceRefusal(
      `${SIDE_INSTANCE_FLAG} must be exactly "${SIDE_INSTANCE_FLAG_VALUE}"; got ${JSON.stringify(flag)}.`,
    );
  }
  if (!isDevelopmentEnv(env)) {
    throw new SideInstanceRefusal(
      `a side instance runs only under NODE_ENV=development (dev-test login on, nothing deployed), and ` +
        `${describeNodeEnv(env)}. Unset ${SIDE_INSTANCE_FLAG} in any deployed or test runtime.`,
    );
  }
  return true;
}

/**
 * Is this process a side instance — and if so, on which port? The WHOLE declaration, as the boot judges it.
 *
 * @param {NodeJS.ProcessEnv} [env=processEnv()]
 * @returns {Readonly<{side: boolean, port: ?number}>} `{ side: false, port: null }` when the flag is absent;
 *   `{ side: true, port }` for a well-formed declaration
 * @throws {SideInstanceRefusal} everything {@link isSideInstance} refuses; a `PORT` that is unset or outside
 *   {@link SIDE_API_PORT_RANGE}
 */
function resolveSideInstance(env = processEnv()) {
  if (!isSideInstance(env)) {
    return Object.freeze({ side: false, port: null });
  }
  const rawPort = env.PORT;
  if (rawPort === undefined || rawPort === '') {
    throw new SideInstanceRefusal(
      `PORT is unset, so the instance would take its app's standard port. Set PORT inside ${RANGE_LABEL}.`,
    );
  }
  return Object.freeze({
    side: true,
    port: parseSidePort(rawPort, `PORT ${JSON.stringify(rawPort)}`),
  });
}

/**
 * The side API port a web dev server proxies its own API to, when one is declared.
 *
 * @param {NodeJS.ProcessEnv} [env=processEnv()]
 * @returns {?number} the declared port, or `null` when {@link SIDE_API_PORT_ENV} is absent
 * @throws {SideInstanceRefusal} a declared value outside {@link SIDE_API_PORT_RANGE}
 */
function resolveSideApiPort(env = processEnv()) {
  const raw = env[SIDE_API_PORT_ENV];
  if (raw === undefined || raw === '') {
    return null;
  }
  return parseSidePort(raw, `${SIDE_API_PORT_ENV} ${JSON.stringify(raw)}`);
}

module.exports = {
  SIDE_INSTANCE_FLAG,
  SIDE_INSTANCE_FLAG_VALUE,
  SIDE_API_PORT_ENV,
  SIDE_API_PORT_RANGE,
  SideInstanceRefusal,
  isSideApiPort,
  isSideInstance,
  resolveSideInstance,
  resolveSideApiPort,
};
